/**
 * Flotte automobile (`/flotte` — fiches 22756 / 22987): page header « Flotte
 * automobile » with the « {n} véhicules actifs · {n} sortis » subtitle
 * (counted from `VEH_SITUATION` over the rows of the current search), then
 * the configurable fleet list (`extranet.fleetListCard`: search card +
 * vehicles table, row → vehicle). The card reports its search through
 * `onFiltersChange`; `WS2_EA_TAB_GEN_VEH` is re-fetched with it as `query`.
 *
 * « Ajouter un véhicule » is out of scope (no spec) — no button rendered.
 * The rule context is the extranet base bag (no page-specific variable).
 */

import { useState } from 'react';
import { useParams } from 'react-router-dom';

import { RuleContextProvider } from '@components/shared/configurable';

import type { FleetListFilters } from '../api/fleetService';
import { PageHeader } from '../components/PageHeader';
import { FLEET_LIST_TEXT } from '../components/fleet/fleetText';
import { FleetListCardConfigurable as FleetListCard } from '../components/fleet/register';
import { useFleetVehicles } from '../components/fleet/useFleetQueries';
import { countFleetVehicles } from '../components/fleet/vehicleStatus';
import { useExtranetBaseRuleContext } from '../contextVariables';
import { EXTRANET_PAGE_TEXT, extranetPath } from '../navigation';

const NO_FILTERS: FleetListFilters = {};

export function FleetPage() {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const ruleContext = useExtranetBaseRuleContext();
  // Search of the list card's toolbar (server-side `query`, fiche 22987).
  const [filters, setFilters] = useState<FleetListFilters>(NO_FILTERS);
  const fleet = useFleetVehicles(filters);
  const text = EXTRANET_PAGE_TEXT.fleet;

  const hasVehicles = !fleet.loading && fleet.error === null && fleet.data.length > 0;
  const subtitle = hasVehicles
    ? FLEET_LIST_TEXT.subtitle(countFleetVehicles(fleet.data))
    : undefined;

  return (
    <RuleContextProvider value={ruleContext}>
      <div data-testid="extranet-page-fleet">
        <PageHeader
          title={text.title}
          subtitle={subtitle}
          backTo={extranetPath(tenantSlug)}
          breadcrumbs={[{ key: 'flotte', label: text.breadcrumb }]}
        />
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <FleetListCard
            vehicles={fleet.data}
            loading={fleet.loading}
            error={fleet.error}
            onFiltersChange={setFilters}
          />
        </div>
      </div>
    </RuleContextProvider>
  );
}
